<script setup lang="ts">
import type { Component } from 'vue';
import { storeToRefs } from 'pinia';
import 'swiper/css/navigation';
import { useAuthStore } from '@/stores/auth';
import { useLayoutStore } from '@/stores/layout';
import { useSmarticoMissions } from '@/stores/smartico/missions';
import { useResponsiveBannerUrl } from '~/composables/apostatudo/use-responsive-banner-url';
import { staticPromocodes } from '~/utils/staticPromocodes';
import type {
  AffiliateConfig,
  AffiliateDetailsConfig,
  BannerItem,
} from '~/utils/perfilOficialConfigs';

const props = withDefaults(
  defineProps<{
    banner: BannerItem[];
    affiliateDetails?: AffiliateDetailsConfig;
    config: AffiliateConfig;
    isRoletasPage: boolean;
    promocode?: string;
    accentColor?: string;
    hidePromocode?: boolean;
    aspectRatio?: string;
    desktopSlidesPerView?: number;
  }>(),
  {
    aspectRatio: '1240 / 541',
    desktopSlidesPerView: 1.55,
  },
);

const bannerCssVars = computed(() => {
  const sideRatio = (1 - 1 / props.desktopSlidesPerView) / 2;

  return {
    '--banner-aspect-ratio': props.aspectRatio,
    '--banner-nav-offset': `${(sideRatio * 95).toFixed(2)}%`,
  } as Record<string, string>;
});

const { isMobile } = useLayout();
const { getLoadingStrategy } = useImageLoading();
const { getResponsiveBannerUrl, getResponsiveBannerUrlWithWidth } =
  useResponsiveBannerUrl();

const avatarVariant = computed(
  () => props.affiliateDetails?.avatarVariant ?? 'circle',
);

const avatarBorderColor = computed(() => {
  if (props.accentColor) return props.accentColor;

  return props.isRoletasPage ? '#FFD700' : '#00C9FF';
});

const avatarGradient = computed(() => {
  if (props.accentColor) {
    return `background: radial-gradient(65.23% 65.23% at 50.31% 53.73%, #FFF 0%, ${props.accentColor} 45.19%, ${props.accentColor}80 100%)`;
  }

  return props.isRoletasPage
    ? 'background: radial-gradient(65.23% 65.23% at 50.31% 53.73%, #FFF 0%, #FFD700 45.19%, #A0522D 100%)'
    : 'background: radial-gradient(65.23% 65.23% at 50.31% 53.73%, #FFF 0%, #00C9FF 45.19%, #0003BE 100%)';
});

const promocode = ref(props.promocode || '');

const isPromocodeButtonActive = computed(() =>
  staticPromocodes.includes(promocode.value),
);

const { loadMissions } = useSmarticoMissions();
const { openToaster } = useLayoutStore();

const authStore = useAuthStore();
const { isAuthenticated } = storeToRefs(authStore);

onMounted(() => {
  loadMissions();
});

function applyPromocode() {
  if (staticPromocodes.includes(promocode.value)) {
    if (window && window._smartico) {
      window._smartico.action(promocode.value);
      promocode.value = '';
    } else {
      openToaster({
        message: 'Smartico não disponível. Promocode não aplicado.',
        type: 'warning',
      });
    }
  } else {
    openToaster({
      message: 'Promocode inválido.',
      type: 'error',
    });
  }
}

// Lazy load Swiper (mesma estratégia do SliderApostatudo/InformationBanners)
const Swiper = shallowRef<Component | null>(null);
const SwiperSlide = shallowRef<Component | null>(null);
const Navigation = shallowRef<any>(null);
const Autoplay = shallowRef<any>(null);
const swiperLoaded = ref(false);

onBeforeMount(async () => {
  const [swiperVue, swiperModules, swiperElement] = await Promise.all([
    import('swiper/vue'),
    import('swiper/modules'),
    import('swiper/element/bundle'),
  ]);

  Swiper.value = swiperVue.Swiper;
  SwiperSlide.value = swiperVue.SwiperSlide;
  Navigation.value = swiperModules.Navigation;
  Autoplay.value = swiperModules.Autoplay;
  swiperElement.register();
  swiperLoaded.value = true;
});

const breakpoints = computed(() => ({
  0: { slidesPerView: 1.2, spaceBetween: 10 },
  768: { slidesPerView: props.desktopSlidesPerView, spaceBetween: 16 },
}));

// Swiper precisa de pelo menos slidesPerView * 2 + 1 slides reais pra o loop não "pular".
const loopThreshold = computed(() =>
  Math.ceil(props.desktopSlidesPerView * 2 + 1),
);

// Se não tem slides suficientes, duplicamos/triplicamos pra o loop não "pular".
const displayBanner = computed(() => {
  if (props.banner.length === 0) return props.banner;
  if (props.banner.length >= loopThreshold.value) return props.banner;

  const copies = Math.ceil(loopThreshold.value / props.banner.length);

  return Array.from({ length: copies }, () => props.banner).flat();
});

const shouldEnableLoop = computed(() => props.banner.length >= 2);

function filterActionBanner(linkUrl: string | null | undefined) {
  if (!linkUrl) return '/';
  if (linkUrl.startsWith('/')) return linkUrl;

  if (linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
    try {
      const url = new URL(linkUrl);

      if (url.hostname.includes('apostatudo.bet')) {
        return url.pathname + url.search + url.hash;
      }

      return linkUrl;
    } catch {
      return linkUrl;
    }
  }

  return linkUrl;
}

function isExternalLink(linkUrl: string | null | undefined) {
  if (!linkUrl) return false;
  if (linkUrl.startsWith('/')) return false;

  if (linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
    try {
      const url = new URL(linkUrl);

      return !url.hostname.includes('apostatudo.bet');
    } catch {
      return true;
    }
  }

  return false;
}

function getBannerSrc(image: string) {
  return isMobile.value
    ? getResponsiveBannerUrlWithWidth(image)
    : getResponsiveBannerUrl(image);
}
</script>

<template>
  <div
    class="banner-home-wrapper select-none"
    :style="bannerCssVars"
  >
    <!-- Carrossel (desktop = SliderApostatudo / mobile = InformationBanners) -->
    <div
      v-if="banner.length > 0"
      class="banner-carousel"
      :class="isMobile ? 'banner-carousel--mobile' : 'banner-carousel--desktop'"
    >
      <div
        v-if="swiperLoaded"
        class="banner-slider"
      >
        <component
          :is="Swiper"
          :autoplay="{
            delay: 6000,
            pauseOnMouseEnter: true,
            disableOnInteraction: false,
          }"
          :breakpoints="!isMobile ? breakpoints : undefined"
          :centered-slides="true"
          class="perfilSwiper h-full"
          create-elements
          :loop="shouldEnableLoop"
          :modules="[Navigation, Autoplay]"
          :navigation="{
            prevEl: '.perfil-swiper-prev',
            nextEl: '.perfil-swiper-next',
          }"
          preload-images="false"
          :slides-per-group="1"
          :slides-per-view="isMobile ? 1.2 : undefined"
          :space-between="isMobile ? 4 : undefined"
          :speed="500"
        >
          <component
            :is="SwiperSlide"
            v-for="(bannerItem, index) in displayBanner"
            :key="`${bannerItem.image}-${index}`"
            class="h-full"
          >
            <NuxtLink
              class="block h-full"
              :external="isExternalLink(bannerItem.linkUrl)"
              no-prefetch
              :target="isExternalLink(bannerItem.linkUrl) ? '_blank' : '_self'"
              :to="filterActionBanner(bannerItem.linkUrl)"
            >
              <img
                :alt="bannerItem.alt || `Banner ${index}`"
                class="rounded-xl object-cover w-full h-full"
                :class="[bannerItem.linkUrl ? 'cursor-pointer' : '']"
                :loading="getLoadingStrategy(index, 1)"
                :src="getBannerSrc(bannerItem.image)"
              />
            </NuxtLink>
          </component>
        </component>

        <div
          class="perfil-swiper-prev navigation-button navigation-button-prev"
        >
          <NuxtIcon
            :filled="true"
            name="arrows/chevron_left"
          />
        </div>
        <div
          class="perfil-swiper-next navigation-button navigation-button-next"
        >
          <NuxtIcon
            :filled="true"
            name="arrows/chevron_right"
          />
        </div>
      </div>

      <div
        v-else
        class="banner-skeleton"
      ></div>
    </div>

    <!-- Affiliate section: avatar + nome/title abaixo do slide ativo -->
    <div
      v-if="affiliateDetails && !config.hideAffiliateBar"
      class="affiliate-bar"
    >
      <div class="flex items-center gap-3 md:gap-4">
        <!-- Avatar redondo (perfis comuns) -->
        <div
          v-if="avatarVariant !== 'icon'"
          class="affiliate-avatar affiliate-avatar--circle"
          :style="[{ borderColor: avatarBorderColor }, avatarGradient]"
        >
          <NuxtImg
            v-if="affiliateDetails.avatarUrl"
            alt="Avatar Affiliate"
            class="w-full h-full object-cover"
            draggable="false"
            :src="affiliateDetails.avatarUrl"
          />
        </div>

        <!-- Avatar como ícone livre (taça etc.) -->
        <div
          v-else
          class="affiliate-avatar affiliate-avatar--icon"
        >
          <NuxtImg
            v-if="affiliateDetails.avatarUrl"
            alt="Avatar Affiliate"
            class="w-full h-full object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.5)]"
            draggable="false"
            :src="affiliateDetails.avatarUrl"
          />
        </div>

        <div>
          <div class="flex items-center gap-1.5 mb-0.5">
            <h1 class="text-white text-sm md:text-lg font-bold leading-7">{{
              affiliateDetails.name
            }}</h1>
            <NuxtIcon
              v-if="affiliateDetails.iconName"
              class="text-white text-[20px]"
              :filled="true"
              :name="affiliateDetails.iconName"
            />
          </div>
          <p
            class="text-[rgba(255,255,255,0.5)] text-xs md:text-sm font-medium leading-[18px]"
            >{{ affiliateDetails.title }}</p
          >
        </div>
      </div>

      <!-- Promocode (desktop, à direita do bloco affiliate) -->
      <div
        v-if="isAuthenticated && config.showPromocodeInput && !hidePromocode"
        class="hidden lg:block h-[64px] shrink-0 rounded-[10px] p-[10px] border backdrop-blur-[10px] border-solid border-[#1D2329]"
      >
        <div
          class="flex items-center gap-2.5 w-full h-full [background:rgba(199,199,199,0.05)] rounded-md"
        >
          <input
            v-model="promocode"
            class="flex-grow h-[44px] rounded-md px-[20px] sm:text-[13px] text-[10px] font-normal text-[#B0BED9] overflow-hidden text-ellipsis whitespace-nowrap min-w-[230px] xl:min-w-[330px]"
            :class="isRoletasPage ? 'bg-[#1A1A1A]' : 'bg-[#0C1B44]'"
            :placeholder="affiliateDetails?.promoCodePlaceholder"
            type="text"
            @keyup.enter="applyPromocode"
          />
          <button
            class="flex justify-center items-center gap-[7px] self-stretch px-4 py-2.5 rounded-md text-center text-sm font-extrabold leading-3"
            :class="
              isPromocodeButtonActive
                ? '[background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] border-[#FC9984] text-white'
                : '[background:#303747] text-[#B7B7B7]'
            "
            :disabled="!isPromocodeButtonActive"
            @click="applyPromocode"
          >
            {{ affiliateDetails?.redeemButtonText }}
          </button>
        </div>
      </div>
    </div>

    <!-- Promocode desktop standalone (quando a barra affiliate está oculta) -->
    <div
      v-if="
        isAuthenticated &&
        config.showPromocodeInput &&
        config.hideAffiliateBar &&
        affiliateDetails &&
        !hidePromocode
      "
      class="hidden lg:flex justify-end mb-4"
    >
      <div
        class="h-[64px] shrink-0 rounded-[10px] p-[10px] border backdrop-blur-[10px] border-solid border-[#1D2329]"
      >
        <div
          class="flex items-center gap-2.5 w-full h-full [background:rgba(199,199,199,0.05)] rounded-md"
        >
          <input
            v-model="promocode"
            class="flex-grow h-[44px] rounded-md px-[20px] sm:text-[13px] text-[10px] font-normal text-[#B0BED9] overflow-hidden text-ellipsis whitespace-nowrap min-w-[230px] xl:min-w-[330px]"
            :class="isRoletasPage ? 'bg-[#1A1A1A]' : 'bg-[#0C1B44]'"
            :placeholder="affiliateDetails?.promoCodePlaceholder"
            type="text"
            @keyup.enter="applyPromocode"
          />
          <button
            class="flex justify-center items-center gap-[7px] self-stretch px-4 py-2.5 rounded-md text-center text-sm font-extrabold leading-3"
            :class="
              isPromocodeButtonActive
                ? '[background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] border-[#FC9984] text-white'
                : '[background:#303747] text-[#B7B7B7]'
            "
            :disabled="!isPromocodeButtonActive"
            @click="applyPromocode"
          >
            {{ affiliateDetails?.redeemButtonText }}
          </button>
        </div>
      </div>
    </div>

    <!-- Promocode mobile -->
    <div
      v-if="isAuthenticated && config.showPromocodeInput && !hidePromocode"
      class="h-[32px] shrink-0 mb-4 w-full block lg:hidden"
    >
      <div class="flex items-center gap-2.5 w-full h-full rounded-md">
        <input
          v-model="promocode"
          class="flex-grow h-full rounded-md px-[20px] sm:text-[13px] text-[10px] font-normal text-[#B0BED9]"
          :class="isRoletasPage ? 'bg-[#1A1A1A]' : 'bg-[#0C1B44]'"
          :placeholder="affiliateDetails?.promoCodePlaceholder"
          type="text"
          @keyup.enter="applyPromocode"
        />
        <button
          class="flex justify-center items-center gap-[7px] self-stretch px-[30px] py-1 sm:px-4 sm:py-2.5 sm:w-[220px] rounded-md text-center text-[10px] sm:text-sm font-extrabold leading-3"
          :class="
            isPromocodeButtonActive
              ? '!text-white [background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] border-[#FC9984]'
              : 'text-[#B7B7B7] [background:#303747]'
          "
          :disabled="!isPromocodeButtonActive"
          @click="applyPromocode"
        >
          {{ affiliateDetails?.redeemButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.banner-home-wrapper {
  @apply mb-2 -mt-3.5 sm:-mt-2 md:-mt-1 lg:-mt-1;
}

.banner-carousel {
  @apply relative overflow-hidden;
  min-height: 122px;
}

.banner-carousel--mobile {
  margin-left: calc(-1 * var(--container-padding, 16px));
  margin-right: calc(-1 * var(--container-padding, 16px));
  @apply mb-3 sm:mb-4;
}

.banner-carousel--desktop {
  @apply mb-3;
}

.banner-slider {
  @apply relative w-full mx-auto;
}

.banner-skeleton {
  min-height: 250px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0.75rem;
}

.perfilSwiper {
  @apply w-full;

  :deep(.swiper-wrapper) {
    align-items: center;
  }

  :deep(.swiper-button-next),
  :deep(.swiper-button-prev) {
    display: none;
  }

  :deep(.swiper-slide) {
    opacity: 0.4;
    z-index: 1;
    transition: all 0.4s ease;
    border-radius: 0.75rem;
    overflow: hidden;
    aspect-ratio: var(--banner-aspect-ratio, 1240 / 541);
    transform: scaleY(0.82);
  }

  :deep(.swiper-slide-active) {
    opacity: 1;
    z-index: 2;
    transform: scaleY(1);
    box-shadow:
      -60px 0 40px -1px rgba(0, 0, 0, 0.9),
      60px 0 40px -1px rgba(0, 0, 0, 0.9);
  }

  @media (max-width: 767px) {
    :deep(.swiper-slide-active) {
      box-shadow:
        -16px 0 20px -1px rgba(0, 0, 0, 0.5),
        16px 0 20px -1px rgba(0, 0, 0, 0.5);
    }
  }
}

// Botões de navegação (estilo SliderApostatudo/InformationBanners)
.navigation-button {
  @apply absolute z-10 flex items-center justify-center
         rounded-[5px] shadow-lg text-texts font-extrabold cursor-pointer;

  width: 25px;
  height: 25px;
  top: 50%;
  transform: translateY(-50%);
  background: var(
    --ATNB---ORANGE,
    radial-gradient(168.3% 138% at 36.76% -16.67%, #ff704a 0%, #ff3400 100%)
  );

  &.navigation-button-prev {
    left: 5%;
  }

  &.navigation-button-next {
    right: 5%;
  }

  @media (min-width: 640px) {
    width: 32px;
    height: 32px;
  }

  @media (min-width: 768px) {
    width: 36px;
    height: 36px;

    &.navigation-button-prev {
      left: var(--banner-nav-offset, 26%);
    }

    &.navigation-button-next {
      right: var(--banner-nav-offset, 26%);
    }
  }
}

.affiliate-bar {
  @apply flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 mt-2 md:mt-3 mb-3 md:mb-4 px-1;
}

.affiliate-avatar {
  @apply flex-shrink-0;
  width: 70px;
  height: 70px;

  @media (min-width: 640px) {
    width: 96px;
    height: 96px;
  }
}

.affiliate-avatar--circle {
  @apply rounded-full border-2 border-solid overflow-hidden;
}

.affiliate-avatar--icon {
  @apply flex items-center justify-center;
}
</style>
